import { useId, useMemo, useState } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Alert,
  Flex,
  PrimaryButton,
  type SegmentedControlOption,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import { DsSelect, type DsSelectOption } from '~/components/common/DsSelect'
import { FieldLabel, FieldRoot } from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { type CivilDate, compareCivilDate } from '~/models/civilDate'
import { useGetPartnerSplitSupplyNoticeFormOptions } from '~/pages/SplitSupplyPlan/hooks/useGetPartnerSplitSupplyNoticeFormOptions'
import { partnerNoticeDetailSearch } from '~/pages/SplitSupplyPlan/models/partnerNotice'
import {
  SECTION_OF_TAB,
  SECTION_TABS,
  type SectionTab,
  addMonths,
  earliestEditableTarget,
  monthOptionValue,
  parseMonthOptionValue,
} from '~/pages/SplitSupplyPlan/models/period'
import { routePaths } from '~/routes/routePaths'

/** 対象月の選択肢: 今月から前後 12 か月 (過去の月も登録できる)。 */
const MONTHS_BEFORE = 12
const MONTHS_AFTER = 12

export type AddPartnerNoticeInput = {
  tab: SectionTab
  /** 翌日は受渡日、 月間は対象月の 1 日。 */
  targetDate: CivilDate
  areaCode: AreaCode
  partnerBusinessMasterId: string
}

/** 追加しようとした条件と同じ条件の登録が既にあった時の、 その登録 (id) と追加した時の条件。 */
export type ExistingPartnerNotice = {
  id: string
  input: AddPartnerNoticeInput
}

type Props = {
  isOpen: boolean
  onClose: () => void
  /** 開いた時の断面・対象日・エリアの初期値 (一覧の現在の選択)。 */
  initialTab: SectionTab
  initialTarget: CivilDate
  initialArea: AreaCode | undefined
  /** 今日 (JST)。 対象月の選択肢の基準。 */
  today: CivilDate
  isCreating: boolean
  onCreate: (input: AddPartnerNoticeInput) => void
  /**
   * 同じ条件の登録が既にあった時のその登録。 今選んでいる条件が追加した時の条件と同じ間だけ、
   * 「この条件の登録は既にあります」 と 「登録を開く」 リンクを出す (条件を変えたら消える)。
   */
  existing: ExistingPartnerNotice | undefined
}

const sameCondition = (
  a: AddPartnerNoticeInput,
  b: AddPartnerNoticeInput,
): boolean =>
  a.tab === b.tab &&
  a.areaCode === b.areaCode &&
  a.partnerBusinessMasterId === b.partnerBusinessMasterId &&
  a.targetDate.year === b.targetDate.year &&
  a.targetDate.month === b.targetDate.month &&
  a.targetDate.day === b.targetDate.day

const firstOfMonth = (d: CivilDate): CivilDate => ({
  year: d.year,
  month: d.month,
  day: 1,
})

/**
 * 他社の通告値の追加のダイアログ。 断面 (翌日 / 月間)・受渡日 (月間は対象月)・エリア・他社を選ぶと、
 * そのエリアでその他社が通告値を出す契約の全地点を持つ空の登録を作る。 受渡日は過去も選べる。
 * 同じ条件の登録が既にある時は作らず、 ダイアログの中に案内と 「登録を開く」 リンクを出す (画面は移らない)。
 * 他社の候補は受渡日 (月間は月初) で判定した契約から backend が返す。 そのエリアに他社が通告値を出す
 * 契約が無ければ理由を出して作れない。
 */
export const AddPartnerNoticeDialog = ({
  isOpen,
  onClose,
  initialTab,
  initialTarget,
  initialArea,
  today,
  isCreating,
  onCreate,
  existing,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const idBase = useId()
  const [tab, setTab] = useState<SectionTab>(initialTab)
  const [target, setTarget] = useState<CivilDate>(
    initialTab === 'monthly' ? firstOfMonth(initialTarget) : initialTarget,
  )
  const [area, setArea] = useState<AreaCode | undefined>(initialArea)
  const [partnerId, setPartnerId] = useState<string | undefined>(undefined)

  const {
    data: options,
    isLoading,
    isError,
  } = useGetPartnerSplitSupplyNoticeFormOptions({
    section: SECTION_OF_TAB[tab],
    targetDate: target,
    enabled: isOpen,
  })
  const partners = useMemo(
    () => options?.areas.find((a) => a.areaCode === area)?.partners ?? [],
    [options, area],
  )
  // 他社は選んだものを使い、 選んだものが候補に無ければ (エリアや日を変えた時) 先頭にする
  const selectedPartner =
    partners.find((p) => p.businessMasterId === partnerId) ?? partners[0]
  const noPartners =
    area !== undefined && options !== undefined && partners.length === 0

  const areaOptions: DsSelectOption[] = AREA_CODE_OPTIONS.map((areaCode) => ({
    value: String(areaCode),
    label: convertAreaLabel(areaCode),
  }))
  const monthOptions: DsSelectOption[] = useMemo(
    () =>
      Array.from({ length: MONTHS_BEFORE + 1 + MONTHS_AFTER }, (_, i) => {
        const month = addMonths(today, i - MONTHS_BEFORE)
        return {
          value: monthOptionValue(month),
          label: t('page:splitSupplyPlan.monthLabel', {
            year: month.year,
            month: month.month,
          }),
        }
      }),
    [today, t],
  )
  const sectionOptions: Array<SegmentedControlOption<SectionTab>> =
    SECTION_TABS.map((key) => ({
      value: key,
      label: t(`page:splitSupplyPlan.tab.${key}`),
    }))
  const partnerOptions: Array<SegmentedControlOption<string>> = partners.map(
    (p) => ({
      value: p.businessMasterId,
      label: `${p.businessCode} ${p.businessName}`,
    }),
  )

  const currentInput: AddPartnerNoticeInput | undefined =
    area === undefined || selectedPartner === undefined
      ? undefined
      : {
          tab,
          targetDate: target,
          areaCode: area,
          partnerBusinessMasterId: selectedPartner.businessMasterId,
        }
  const existingForCurrent =
    existing !== undefined &&
    currentInput !== undefined &&
    sameCondition(existing.input, currentInput)
      ? existing
      : undefined

  // 過ぎた時間 (受渡日が今日以前・月間は対象月が今月以前) の通告値は、 取り込み済みの実績に反映されない。
  // 2a の計画作成が選べる最も早い対象日 (明日・来月) を決めるのと同じ helper で、 それより前を過ぎた時間とする
  const isPastTarget =
    compareCivilDate(target, earliestEditableTarget(tab, today)) < 0

  // 断面を切り替えたら、 その断面で作成・削除できる最初の対象日 (翌日は明日、 月間は来月) に寄せる
  const changeTab = (next: SectionTab): void => {
    setTab(next)
    setTarget(earliestEditableTarget(next, today))
  }

  const targetId = `${idBase}-target`
  const areaId = `${idBase}-area`
  const partnerLabelId = `${idBase}-partner`

  const footer = (
    <Flex justifyContent="flex-end" gap={2} width="100%">
      <TertiaryButton onClick={onClose} size="md">
        {t('common:action.cancel')}
      </TertiaryButton>
      <PrimaryButton
        disabled={
          area === undefined || selectedPartner === undefined || isCreating
        }
        loading={isCreating}
        onClick={() => {
          if (area !== undefined && selectedPartner !== undefined) {
            onCreate({
              tab,
              targetDate: target,
              areaCode: area,
              partnerBusinessMasterId: selectedPartner.businessMasterId,
            })
          }
        }}
        size="md"
      >
        {t('page:splitSupplyPlan.partner.addDialog.submit')}
      </PrimaryButton>
    </Flex>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={t('page:splitSupplyPlan.partner.addDialog.title')}
    >
      <Flex flexDirection="column" gap={4}>
        <Text textStyle="caption" color="Text.midEmphasis">
          {t('page:splitSupplyPlan.partner.addDialog.note')}
        </Text>
        <FieldRoot>
          <FieldLabel>
            {t('page:splitSupplyPlan.partner.addDialog.section')}
          </FieldLabel>
          <AccessibleSegmentedControl
            aria-label={t('page:splitSupplyPlan.partner.addDialog.section')}
            name="add-partner-notice-section"
            onSelect={changeTab}
            options={sectionOptions}
            selected={tab}
          />
        </FieldRoot>
        <FieldRoot>
          <FieldLabel htmlFor={targetId}>
            {t(
              tab === 'monthly'
                ? 'page:splitSupplyPlan.createDialog.targetMonth'
                : 'page:splitSupplyPlan.createDialog.targetDate',
            )}
          </FieldLabel>
          {tab === 'monthly' ? (
            <DsSelect
              aria-label={t('page:splitSupplyPlan.createDialog.targetMonth')}
              inputId={targetId}
              onChange={(value) => {
                const month = parseMonthOptionValue(value)
                if (month !== undefined) setTarget(month)
              }}
              options={monthOptions}
              value={monthOptionValue(target)}
            />
          ) : (
            <CompactCivilDateField
              aria-label={t('page:splitSupplyPlan.createDialog.targetDate')}
              id={targetId}
              onChange={(d) => {
                if (d !== undefined) setTarget(d)
              }}
              value={target}
            />
          )}
        </FieldRoot>
        {isPastTarget && (
          <Alert
            description={t('page:splitSupplyPlan.partner.pastWarning')}
            status="warning"
          />
        )}
        <FieldRoot>
          <FieldLabel htmlFor={areaId}>
            {t('page:splitSupplyPlan.createDialog.area')}
          </FieldLabel>
          <DsSelect
            aria-label={t('page:splitSupplyPlan.createDialog.area')}
            inputId={areaId}
            onChange={(value) => {
              setArea(AREA_CODE_OPTIONS.find((a) => String(a) === value))
            }}
            options={areaOptions}
            placeholder={t('common:form.selectPlaceholder')}
            value={area === undefined ? undefined : String(area)}
          />
        </FieldRoot>
        <FieldRoot>
          <FieldLabel id={partnerLabelId}>
            {t('page:splitSupplyPlan.partner.addDialog.counterparty')}
          </FieldLabel>
          {isError ? (
            <Alert
              description={t(
                'page:splitSupplyPlan.partner.addDialog.optionsFailed',
              )}
              status="error"
            />
          ) : noPartners ? (
            <Text textStyle="caption" color="Text.negativeMid">
              {t('page:splitSupplyPlan.partner.addDialog.noPartners')}
            </Text>
          ) : partnerOptions.length === 0 ? (
            <Text textStyle="caption" color="Text.midEmphasis">
              {t(
                isLoading
                  ? 'page:splitSupplyPlan.partner.addDialog.loading'
                  : 'page:splitSupplyPlan.partner.addDialog.selectArea',
              )}
            </Text>
          ) : (
            <AccessibleSegmentedControl
              aria-label={t(
                'page:splitSupplyPlan.partner.addDialog.counterparty',
              )}
              name="add-partner-notice-counterparty"
              onSelect={setPartnerId}
              options={partnerOptions}
              selected={selectedPartner?.businessMasterId ?? ''}
            />
          )}
        </FieldRoot>
        {existingForCurrent !== undefined && (
          <Alert
            description={
              <Flex alignItems="center" gap={3}>
                <span>
                  {t('page:splitSupplyPlan.partner.addDialog.alreadyExists')}
                </span>
                <TertiaryButton asChild size="sm">
                  <RouterLink
                    to={`${routePaths.planSplitSupplyDetail.path}?${partnerNoticeDetailSearch(existingForCurrent.id, existingForCurrent.input.targetDate)}`}
                  >
                    {t('page:splitSupplyPlan.partner.addDialog.openExisting')}
                  </RouterLink>
                </TertiaryButton>
              </Flex>
            }
            status="warning"
          />
        )}
      </Flex>
    </Modal>
  )
}
